import { useCallback, useEffect, useRef, useState } from 'react';
import { buildGraph } from './graphModel';
import { indexGraph } from './graphQueries';
import { readAppGraphSnapshot } from './graphAppSource';
import { useGraphUiStore } from './graphUiStore';
import { useCollectionsStore } from '../store/collectionsStore';
import { useVariablesStore } from '../store/variablesStore';
import { useBoardsUiStore } from '../extensions/bundled/task-manager/boardsUiStore';
import { useDocumentsUiStore } from '../extensions/bundled/documents/documentsUiStore';
import { useCalendarUiStore } from '../extensions/bundled/calendar/calendarUiStore';
import { useVaultStore, VAULT_GATE_UNLOCKED_EVENT, VAULT_LOCKED_UI_EVENT, VAULT_UNLOCKED_UI_EVENT } from '../store/vaultStore';
import { useAuthStore } from '../store/authStore';
import { useTeamsStore } from '../store/teamsStore';

const REBUILD_DEBOUNCE_MS = 500;

/**
 * The live link graph for a page. Rebuilds — debounced, so typing in a
 * request does not rebuild per keystroke — whenever a source announces a
 * change: collections/environments stores, the boards and documents nonces
 * (bumped by UI writes, MCP writes and sync pulls alike), and project/link
 * writes. That is what makes the graph grow on its own.
 *
 * Returns { graph, index, projects, links, unavailable, loading, refresh }.
 * `graph` keeps its identity when nothing changed, so the canvas does not
 * restart its layout for a no-op rebuild.
 */
export function useLinkGraph() {
  const [state, setState] = useState({
    graph: { version: 1, nodes: [], edges: [] },
    index: indexGraph({ nodes: [], edges: [] }),
    projects: [],
    links: [],
    unavailable: [],
    loading: true,
  });
  const signatureRef = useRef('');
  const timerRef = useRef(null);
  const runRef = useRef(0);

  const rebuild = useCallback(async () => {
    const run = ++runRef.current;
    const { snapshot, unavailable } = await readAppGraphSnapshot();
    if (run !== runRef.current) return; // a newer rebuild started meanwhile
    const built = buildGraph(snapshot);
    const signature = graphSignature(built);
    // Decided OUTSIDE the state updater: StrictMode may run an updater twice,
    // and a ref written inside it made the second run keep a stale graph.
    const changed = signature !== signatureRef.current;
    signatureRef.current = signature;
    const fresh = changed ? { graph: built, index: indexGraph(built) } : null;
    setState((prev) => ({
      graph: fresh ? fresh.graph : prev.graph,
      index: fresh ? fresh.index : prev.index,
      projects: snapshot.projects,
      links: snapshot.links,
      unavailable,
      loading: false,
    }));
  }, []);

  const schedule = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(rebuild, REBUILD_DEBOUNCE_MS);
  }, [rebuild]);

  const graphVersion = useGraphUiStore((s) => s.graphVersion);
  const boardsVersion = useBoardsUiStore((s) => s.boardsVersion);
  const treeVersion = useDocumentsUiStore((s) => s.treeVersion);
  const collections = useCollectionsStore((s) => s.collections);
  const environments = useVariablesStore((s) => s.environments);
  const eventsVersion = useCalendarUiStore((s) => s.eventsVersion);
  const vaultItems = useVaultStore((s) => s.items);
  // Sign-in changes the personal scope id, so every records read changes too.
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // The team roster is the gate for team items (graphScopes.js): joining or
  // leaving a team, or the late background teams load, rebuilds the graph.
  const teams = useTeamsStore((s) => s.teams);

  // Lock/unlock can happen anywhere; connection and host names must disappear
  // on lock, not linger until something else changes.
  useEffect(() => {
    window.addEventListener(VAULT_LOCKED_UI_EVENT, schedule);
    window.addEventListener(VAULT_UNLOCKED_UI_EVENT, schedule);
    window.addEventListener(VAULT_GATE_UNLOCKED_EVENT, schedule);
    return () => {
      window.removeEventListener(VAULT_LOCKED_UI_EVENT, schedule);
      window.removeEventListener(VAULT_UNLOCKED_UI_EVENT, schedule);
      window.removeEventListener(VAULT_GATE_UNLOCKED_EVENT, schedule);
    };
  }, [schedule]);

  useEffect(() => {
    rebuild();
    return () => clearTimeout(timerRef.current);
  }, [rebuild]);

  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    // Project/link writes are the user's own action on this page: answer fast.
    schedule();
  }, [graphVersion, boardsVersion, treeVersion, collections, environments, eventsVersion, vaultItems, userId, teams, schedule]);

  return { ...state, refresh: rebuild };
}

/**
 * Identity for "did the graph actually change". The serialised graph itself,
 * not a hash: every field counts (method, engine, anchors…) and two different
 * graphs can never compare equal. Built once per debounced rebuild.
 */
export function graphSignature(graph) {
  return JSON.stringify(graph);
}
